import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Zap, TrendingUp, MessageSquare, Bot, Globe, BarChart3, Sparkles, Users } from "lucide-react";

const LOGO = "/plugitai-logo-256.png";

const FEATURES = [
  "Websites", "CRM", "AI Employees", "SEO",
  "Analytics", "Automation", "Messaging", "Integrations",
];

export default function Login() {
  const { login, error } = useAuth();
  const [params] = useSearchParams();
  const brandSlug = params.get("brand");
  const [brand, setBrand] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!brandSlug) return;
    axios.get(`${API}/public/tenants/${brandSlug}`).then(({ data }) => setBrand(data)).catch(() => {});
  }, [brandSlug]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    if (ok) {
      setSuccess(true);
      setTimeout(() => navigate("/admin"), 700);
    } else {
      setBusy(false);
    }
  };

  // Brand-themed login (unchanged behaviour, minor visual)
  if (brand) return <BrandLogin brand={brand} email={email} setEmail={setEmail} password={password} setPassword={setPassword} onSubmit={submit} busy={busy} error={error} />;

  return (
    <div className={`login-bg min-h-screen flex flex-col relative overflow-hidden transition-opacity duration-500 ${success ? "opacity-50" : "opacity-100"}`}>
      {/* Top logo */}
      <header className="pt-10 pb-4 px-8 relative z-10">
        <div className={`flex items-center gap-3 ${success ? "logo-glow" : ""}`}>
          <img
            src={LOGO}
            alt="PlugItAI — Powered by Nariai · Intelligent Systems. Real Results."
            className="h-36 md:h-44 w-auto rounded-2xl shadow-[0_20px_60px_-20px_rgba(37,99,235,0.35)]"
            style={{ background: "#0B0F14" }}
            data-testid="nariai-logo"
          />
        </div>
      </header>

      <main className="flex-1 grid lg:grid-cols-2 gap-12 px-8 lg:px-16 pb-16 relative z-10">
        {/* LEFT — copy + form */}
        <section className="flex flex-col justify-center max-w-lg w-full mx-auto lg:mx-0">
          <div className="fade-up">
            <div className="text-sm font-medium text-[var(--n-blue)] tracking-wide mb-4">The AI Operating System for Modern Businesses</div>
            <h1 className="font-cab font-black text-5xl md:text-7xl leading-[0.95] tracking-tight text-[var(--n-text)]">
              Dream it. <span className="gradient-tri">Build it.</span>
            </h1>
            <p className="text-lg text-[var(--n-text-sec)] mt-6 leading-relaxed">
              One platform to run your entire business.
            </p>
            <p className="text-base text-[var(--n-text-sec)] mt-3 leading-relaxed">
              <span className="font-semibold text-[var(--n-text)]">And no — we're not expensive.</span> Pricing that fits real small businesses, not enterprise budgets.
            </p>
          </div>

          <form onSubmit={submit} className="mt-10 space-y-3 fade-up delay-2" data-testid="login-form">
            <div className="grid grid-cols-1 gap-3">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="n-input" placeholder="Email address"
                data-testid="login-email-input"
              />
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="n-input" placeholder="Password"
                data-testid="login-password-input"
              />
            </div>
            {error && <div className="text-sm text-[var(--n-danger)] font-medium" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={busy || success} className="n-btn n-btn-blue w-full justify-center py-3 disabled:opacity-70" data-testid="login-submit-button">
              {success ? "Entering Mission Control…" : busy ? "Signing in…" : "Sign in"}
            </button>
            <div className="grid grid-cols-2 gap-3 pt-1">
              <button type="button" className="n-btn n-btn-ghost justify-center" onClick={() => alert("SSO coming soon — use email/password for now.")} data-testid="login-google">Continue with Google</button>
              <button type="button" className="n-btn n-btn-ghost justify-center" onClick={() => alert("SSO coming soon — use email/password for now.")} data-testid="login-microsoft">Continue with Microsoft</button>
            </div>
            <div className="flex justify-between pt-2 text-sm">
              <button type="button" className="text-[var(--n-text-sec)] hover:text-[var(--n-text)]" data-testid="forgot-password">Forgot password?</button>
              <button type="button" className="text-[var(--n-blue)] font-medium hover:underline" data-testid="request-demo">Request demo →</button>
            </div>
          </form>

          <div className="mt-12 fade-up delay-3">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--n-text-muted)] mb-4">Everything, one login</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {FEATURES.map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-[var(--n-text)]">
                  <span className="text-[var(--n-green)]">✔</span> {f}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* RIGHT — floating mission-control preview */}
        <section className="hidden lg:flex items-center justify-center relative">
          <MissionControlPreview />
        </section>
      </main>
    </div>
  );
}

// ---- Floating glass mission-control preview (right side of login) ----
function MissionControlPreview() {
  return (
    <div className="relative w-full max-w-2xl h-[680px]">
      {/* Circuit backdrop */}
      <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 600 600" fill="none">
        <path d="M50 100 Q150 100 200 200 T400 300" stroke="var(--n-blue)" strokeWidth="1.5" className="circuit-trace"/>
        <path d="M550 150 Q400 200 350 350 T100 500" stroke="var(--n-orange)" strokeWidth="1.5" className="circuit-trace"/>
        <path d="M100 400 Q250 450 400 400 T580 500" stroke="var(--n-green)" strokeWidth="1.5" className="circuit-trace"/>
        <circle cx="200" cy="200" r="4" fill="var(--n-blue)"/>
        <circle cx="400" cy="300" r="4" fill="var(--n-blue)"/>
        <circle cx="350" cy="350" r="4" fill="var(--n-orange)"/>
        <circle cx="400" cy="400" r="4" fill="var(--n-green)"/>
      </svg>

      {/* Revenue card */}
      <div className="glass absolute top-4 left-2 w-[240px] rounded-2xl p-5 float-slow" style={{ animationDelay: "0s" }}>
        <div className="text-xs uppercase tracking-wider text-[var(--n-text-muted)]">Revenue · this Summer</div>
        <div className="font-cab font-black text-3xl mt-1 gradient-tri">$142,890</div>
        <div className="flex items-center gap-1 mt-2 text-xs text-[var(--n-green)] font-medium">
          <TrendingUp size={12}/> +18.2% vs last month
        </div>
        <svg viewBox="0 0 100 30" className="mt-3 w-full h-8">
          <polyline points="0,25 15,20 25,22 40,10 55,15 70,8 85,12 100,4" fill="none" stroke="var(--n-blue)" strokeWidth="2"/>
        </svg>
      </div>

      {/* Leads pipeline */}
      <div className="glass absolute top-32 right-0 w-[260px] rounded-2xl p-5 float-slower" style={{ animationDelay: "1s" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-[var(--n-text-muted)]">CRM Pipeline</div>
          <Users size={14} className="text-[var(--n-blue)]"/>
        </div>
        <div className="space-y-2">
          {[["New", 24, "blue"], ["Qualified", 12, "orange"], ["Won", 8, "green"]].map(([label, count, color]) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-sm text-[var(--n-text)]">{label}</span>
              <span className={`n-chip n-chip-${color}`}>{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI conversations */}
      <div className="glass absolute bottom-40 left-0 w-[280px] rounded-2xl p-5 float-slow" style={{ animationDelay: "2s" }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "var(--n-orange-soft)" }}>
            <Bot size={16} className="text-[var(--n-orange)]"/>
          </div>
          <div>
            <div className="text-sm font-medium">AI Employee</div>
            <div className="text-xs text-[var(--n-text-muted)]">resolved 47 chats today</div>
          </div>
        </div>
        <div className="rounded-lg bg-[var(--n-surface)] p-3 text-xs text-[var(--n-text-sec)]">
          &ldquo;Hi! I can transfer your prescription in under 5 minutes…&rdquo;
        </div>
        <div className="flex items-center justify-between mt-3 text-xs">
          <span className="text-[var(--n-green)] font-medium">● 98% CSAT</span>
          <span className="text-[var(--n-text-muted)]">avg 1.2s</span>
        </div>
      </div>

      {/* Website status */}
      <div className="glass absolute bottom-4 right-4 w-[240px] rounded-2xl p-5 float-slower" style={{ animationDelay: "3s" }}>
        <div className="flex items-center gap-2">
          <Globe size={14} className="text-[var(--n-blue)]"/>
          <span className="text-xs uppercase tracking-wider text-[var(--n-text-muted)]">Websites</span>
        </div>
        <div className="font-cab font-black text-2xl mt-1">4 live</div>
        <div className="mt-3 space-y-1">
          {["lonestarstriping.com", "patricksgd.com", "yourdomain.com"].map((d, i) => (
            <div key={d} className="flex items-center gap-2 text-xs">
              <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: "var(--n-green)" }}/>
              <span className="text-[var(--n-text-sec)]">{d}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Automation flow */}
      <div className="glass absolute top-56 left-24 w-[220px] rounded-2xl p-4 float-slow" style={{ animationDelay: "1.5s" }}>
        <div className="flex items-center gap-2 mb-2">
          <Zap size={13} className="text-[var(--n-orange)]"/>
          <span className="text-xs uppercase tracking-wider text-[var(--n-text-muted)]">Automation</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-[var(--n-text)]">
          <span className="n-chip n-chip-blue">Lead</span>
          <span className="text-[var(--n-text-muted)]">→</span>
          <span className="n-chip n-chip-orange">AI</span>
          <span className="text-[var(--n-text-muted)]">→</span>
          <span className="n-chip n-chip-green">SMS</span>
        </div>
      </div>

      {/* SEO score */}
      <div className="glass absolute top-4 right-32 w-[180px] rounded-2xl p-4 float-slower">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 size={13} className="text-[var(--n-green)]"/>
          <span className="text-xs uppercase tracking-wider text-[var(--n-text-muted)]">SEO score</span>
        </div>
        <div className="font-cab font-black text-4xl gradient-tri">92</div>
      </div>
    </div>
  );
}

// White-label brand login (compact fallback)
function BrandLogin({ brand, email, setEmail, password, setPassword, onSubmit, busy, error }) {
  const primary = brand.primary_color || "#0055FF";
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center p-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-cab font-black" style={{ background: primary }}>{brand.name[0]}</div>
            <span className="font-cab font-black text-2xl">{brand.name}</span>
          </div>
          <h1 className="font-cab font-black text-4xl leading-tight">Sign in to your workspace</h1>
          <p className="text-sm text-[var(--n-text-sec)] mt-2">{brand.tagline}</p>
          <form onSubmit={onSubmit} className="mt-8 space-y-3" data-testid="login-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="n-input" placeholder="Email" data-testid="login-email-input" />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="n-input" placeholder="Password" data-testid="login-password-input" />
            {error && <div className="text-sm text-[var(--n-danger)]" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={busy} className="n-btn w-full justify-center py-3" style={{ background: primary }} data-testid="login-submit-button">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
      <div className="hidden lg:flex items-end p-12" style={{ background: primary }}>
        <div className="text-white">
          <div className="font-cab font-black text-5xl leading-tight">{brand.name}</div>
          <div className="font-outfit text-lg mt-3 opacity-90">{brand.tagline}</div>
        </div>
      </div>
    </div>
  );
}
